/**
 * Demo stub for `react-calendly`.
 *
 * Vite's resolve.alias in `vite.config.demo.ts` rewrites `react-calendly` to this module, so nothing
 * in the demo bundle ever loads calendly.com — a prospect can walk the whole "Schedule a call with us"
 * flow (Message Center footer, CSAT survey, insurance manual quote) without landing on a real,
 * bookable calendar.
 *
 * What replaces it is a look-alike of the Calendly embed a real member sees today: the
 * "Select a Date & Time" card, month navigation, availability pills, the time-zone footnote, and the
 * time list that slides in once a day is picked. Confirming a slot records it in
 * `demo/scheduledCall.ts` and fires the `onEventScheduled` callback the real widget posts, so every
 * consumer's success path runs unchanged — the Message Center footer flips to the booked-call summary
 * (prod's `ScheduledView`), analytics fire, queries invalidate.
 *
 * Deliberately styled to mimic a third party, so it reads as "the scheduler" in a demo rather than as
 * a Collective surface. It still draws every color and spacing value from the theme.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';

import { setDemoScheduledCall } from '../scheduledCall';

import ChevronLeftIcon from 'modules/common/components/Icons/ChevronLeftIcon';
import ChevronRightIcon from 'modules/common/components/Icons/ChevronRightIcon';
import GlobeIcon from 'modules/common/components/Icons/GlobeIcon';
import Dialog from 'modules/common/components/MUI/Dialog';

// ---- Event payloads (the shapes react-calendly's listeners receive) ----

type CalendlyEvent<TEvent extends string> = { data: { event: TEvent; payload: Record<string, unknown> } };
export type ProfilePageViewedEvent = CalendlyEvent<'calendly.profile_page_viewed'>;
export type EventTypeViewedEvent = CalendlyEvent<'calendly.event_type_viewed'>;
export type DateAndTimeSelectedEvent = CalendlyEvent<'calendly.date_and_time_selected'>;
export type EventScheduledEvent = CalendlyEvent<'calendly.event_scheduled'>;

interface CalendlyListeners {
    onProfilePageViewed?: (e: ProfilePageViewedEvent) => void;
    onEventTypeViewed?: (e: EventTypeViewedEvent) => void;
    onDateAndTimeSelected?: (e: DateAndTimeSelectedEvent) => void;
    onEventScheduled?: (e: EventScheduledEvent) => void;
}

// Live listener boxes. The real package listens for Calendly's postMessage events, which never
// arrive here — the picker dispatches to these handlers directly instead.
const listeners = new Set<{ current: CalendlyListeners }>();

export function useCalendlyEventListener(handlers: CalendlyListeners) {
    // One stable box per hook instance, kept current by an effect: the handlers argument is a new
    // object literal every render, so registering it directly would churn the listener set.
    const box = useRef<CalendlyListeners>(handlers);
    useEffect(() => {
        box.current = handlers;
    });
    useEffect(() => {
        const registered = box;
        listeners.add(registered);
        return () => {
            listeners.delete(registered);
        };
    }, []);
}

function emitDateAndTimeSelected(startTime: string) {
    listeners.forEach(({ current }) =>
        current.onDateAndTimeSelected?.({
            data: { event: 'calendly.date_and_time_selected', payload: { start_time: startTime } },
        })
    );
}

function emitEventScheduled(startTime: string) {
    listeners.forEach(({ current }) =>
        current.onEventScheduled?.({
            data: {
                event: 'calendly.event_scheduled',
                payload: {
                    event: { uri: 'https://demo.collective.com/scheduled_events/demo', start_time: startTime },
                    invitee: { uri: 'https://demo.collective.com/invitees/demo' },
                },
            },
        })
    );
}

// ---- Generated availability ----

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_OFFERED = 21;
// Minutes past midnight — mirrors a typical specialist's day (30-minute meetings).
const SLOT_MINUTES = [9 * 60, 9 * 60 + 30, 10 * 60, 11 * 60, 13 * 60, 14 * 60, 15 * 60 + 30];

/** Bookable weekdays over the next three weeks, starting tomorrow — never an empty calendar. */
function buildAvailability(): Map<string, Dayjs[]> {
    const availability = new Map<string, Dayjs[]>();
    for (let offset = 1; offset <= DAYS_OFFERED; offset += 1) {
        const date = dayjs().add(offset, 'day');
        const isWeekend = date.day() === 0 || date.day() === 6;
        if (!isWeekend) {
            // Drop the afternoon every third day so availability reads like a real, partly-booked calendar.
            const minutes = offset % 3 === 0 ? SLOT_MINUTES.filter((minute) => minute < 13 * 60) : SLOT_MINUTES;
            availability.set(
                date.format('YYYY-MM-DD'),
                minutes.map((minute) => date.startOf('day').add(minute, 'minute'))
            );
        }
    }
    return availability;
}

/** "Pacific Time - US & Canada (3:13pm)" — Calendly's time-zone footnote. */
function timeZoneLabel(): string {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
    const longName = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'long' })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value;
    // Calendly labels the zone generically ("Pacific Time"), not by the current DST variant.
    const zoneName = (longName ?? zone).replace(/ (Daylight|Standard) /, ' ');
    const region = zone.startsWith('America/') ? 'US & Canada' : zone.split('/')[0].replace('_', ' ');
    return `${zoneName} - ${region} (${dayjs().format('h:mma')})`;
}

// ---- The look-alike card ----

/** The month's grid cells: blanks for the days before the 1st, then every date in the month. */
function monthCells(month: Dayjs): (Dayjs | null)[] {
    const firstOfMonth = month.startOf('month');
    const leadingBlanks = firstOfMonth.day();
    const cells: (Dayjs | null)[] = Array.from({ length: leadingBlanks }, () => null);
    for (let day = 0; day < month.daysInMonth(); day += 1) {
        cells.push(firstOfMonth.add(day, 'day'));
    }
    return cells;
}

interface DayCellProps {
    day: Dayjs;
    isAvailable: boolean;
    isSelected: boolean;
    isToday: boolean;
    onSelect: (day: Dayjs) => void;
}

function DayCell({ day, isAvailable, isSelected, isToday, onSelect }: Readonly<DayCellProps>) {
    return (
        <Stack alignItems="center" spacing={0.25}>
            <Box
                component="button"
                type="button"
                disabled={!isAvailable}
                aria-label={day.format('dddd, MMMM D')}
                aria-pressed={isSelected}
                onClick={() => onSelect(day)}
                sx={{
                    width: 40,
                    height: 40,
                    p: 0,
                    border: 'none',
                    borderRadius: '50%',
                    cursor: isAvailable ? 'pointer' : 'default',
                    fontFamily: 'inherit',
                    fontSize: '0.9375rem',
                    fontWeight: isAvailable ? 600 : 400,
                    transition: 'background-color 0.15s ease',
                    ...(isSelected && { bgcolor: 'primary.main', color: 'primary.contrastText' }),
                    ...(!isSelected &&
                        isAvailable && {
                            bgcolor: 'lightPrimary.hover',
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'lightPrimary.main' },
                        }),
                    ...(!isAvailable && { bgcolor: 'transparent', color: 'text.disabled' }),
                }}>
                {day.format('D')}
            </Box>
            {/* Calendly marks today with a dot under the date. */}
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: isToday ? 'text.primary' : 'transparent' }} />
        </Stack>
    );
}

function CalendlyLookalike() {
    const availability = useMemo(buildAvailability, []);
    const zoneLabel = useMemo(timeZoneLabel, []);
    const firstAvailable = useMemo(() => [...availability.keys()][0], [availability]);

    const [viewMonth, setViewMonth] = useState(() => dayjs(firstAvailable).startOf('month'));
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Dayjs | null>(null);
    const [isBooking, setIsBooking] = useState(false);

    const handleSelectDate = (day: Dayjs) => {
        setSelectedDate(day.format('YYYY-MM-DD'));
        setSelectedSlot(null);
    };

    const handleConfirm = () => {
        if (!selectedSlot) {
            return;
        }
        const startTime = selectedSlot.toISOString();
        // Booking is instant here; the flag just stops a second click while the host closes the
        // dialog and refetches the member's calendars.
        setIsBooking(true);
        setDemoScheduledCall(startTime);
        emitEventScheduled(startTime);
    };

    const daySlots = selectedDate ? availability.get(selectedDate) ?? [] : [];

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} sx={{ pb: 2 }}>
            <Box sx={{ width: { xs: '100%', sm: 320 }, flexShrink: 0 }}>
                <Typography variant="h4" sx={{ mb: 3 }}>
                    Select a Date &amp; Time
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <IconButton
                        size="small"
                        aria-label="Previous month"
                        disabled={!viewMonth.isAfter(dayjs(), 'month')}
                        onClick={() => setViewMonth(viewMonth.subtract(1, 'month'))}>
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6">{viewMonth.format('MMMM YYYY')}</Typography>
                    <IconButton
                        size="small"
                        aria-label="Next month"
                        onClick={() => setViewMonth(viewMonth.add(1, 'month'))}>
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', justifyItems: 'center' }}>
                    {WEEKDAY_LABELS.map((label) => (
                        <Typography key={label} variant="caption" color="text.secondary" sx={{ pb: 1 }}>
                            {label}
                        </Typography>
                    ))}
                    {monthCells(viewMonth).map((day, index) =>
                        day ? (
                            <DayCell
                                key={day.format('YYYY-MM-DD')}
                                day={day}
                                isAvailable={availability.has(day.format('YYYY-MM-DD'))}
                                isSelected={day.format('YYYY-MM-DD') === selectedDate}
                                isToday={day.isSame(dayjs(), 'day')}
                                onSelect={handleSelectDate}
                            />
                        ) : (
                            // eslint-disable-next-line react/no-array-index-key
                            <Box key={`blank-${index}`} sx={{ width: 40, height: 43 }} />
                        )
                    )}
                </Box>
                <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                    Time zone
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <GlobeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                        {zoneLabel}
                    </Typography>
                </Stack>
            </Box>

            {/* Times slide in beside the calendar once a day is picked, like the real embed. */}
            {selectedDate && (
                <Box sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {dayjs(selectedDate).format('dddd, MMMM D')}
                    </Typography>
                    <Stack spacing={1} sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                        {daySlots.map((slot) => {
                            const isSelected = Boolean(selectedSlot && slot.isSame(selectedSlot));
                            return (
                                <Button
                                    key={slot.toISOString()}
                                    fullWidth
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    color="primary"
                                    onClick={() => {
                                        setSelectedSlot(slot);
                                        emitDateAndTimeSelected(slot.toISOString());
                                    }}>
                                    {slot.format('h:mm A')}
                                </Button>
                            );
                        })}
                    </Stack>
                    {selectedSlot && (
                        <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            size="large"
                            loading={isBooking}
                            sx={{ mt: 2 }}
                            onClick={handleConfirm}>
                            Confirm
                        </Button>
                    )}
                </Box>
            )}
        </Stack>
    );
}

// ---- react-calendly's public surface (only what this app imports) ----

interface PopupModalProps {
    open: boolean;
    onModalClose: () => void;
    url?: string;
    rootElement?: HTMLElement;
    [key: string]: unknown;
}

export function PopupModal({ open, onModalClose }: Readonly<PopupModalProps>) {
    return (
        <Dialog open={open} size="medium" showDialogActions={false} onClose={onModalClose}>
            <CalendlyLookalike />
        </Dialog>
    );
}

interface InlineWidgetProps {
    url?: string;
    styles?: { height?: number | string };
    [key: string]: unknown;
}

export function InlineWidget({ styles }: Readonly<InlineWidgetProps>) {
    return (
        <Box sx={{ minHeight: styles?.height }}>
            <CalendlyLookalike />
        </Box>
    );
}
